import React, { useState, useCallback } from 'react';
import {
  Button,
  Input,
  Card,
  Row,
  Col,
  Form,
  message,
  Skeleton,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LeadsTable from '../../components/sales/LeadsTable';
import DiscoveryTab from '../../components/sales/DiscoveryTab';
import type { Lead, Contact, LeadSource } from '../../types';
import LeadModal from '../../components/sales/LeadModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import OpportunityModal from '../../components/sales/OpportunityModal';
import { getLeads, deleteLead, addOrUpdateOpportunity } from '../../api/leads';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_STALE_TIME_MS } from '../../constants/api';

interface FormData {
  name: string;
  status: 'New' | 'Disqualified';
  source: LeadSource;
  address: string;
  city: string;
  pincode: string;
  country: string;
  description?: string;
  notes?: string;
  contacts: {
    name: string;
    position: string;
    email: string;
    phone: string;
    type: Contact['type'];
  }[];
}

const PAGE_SIZE = 10;

const NewLeadsPage: React.FC = () => {
  const { t } = useTranslation(['common', 'sales']);
  const navigate = useNavigate();
  const [form] = Form.useForm<FormData>();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openOpportunityDialog, setOpenOpportunityDialog] = useState(false);
  const [opportunityForm] = Form.useForm();
  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [lastDocs, setLastDocs] = useState<{ [page: number]: QueryDocumentSnapshot | undefined }>({});
  const [hasNext, setHasNext] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const queryClient = useQueryClient();

  // Fetch leads for the current page
  const {
    data: leads = [],
    isLoading,
  } = useQuery<Lead[], Error>({
    queryKey: ['leads', page],
    queryFn: async () => {
      const lastDoc = page > 1 ? lastDocs[page - 1] : undefined;
      const { leads: newLeads, lastDoc: newLastDoc } = await getLeads(PAGE_SIZE, lastDoc);
      setLastDocs(prev => ({ ...prev, [page]: newLastDoc }));
      setHasNext(!!newLastDoc && newLeads.length === PAGE_SIZE);
      return newLeads;
    },
    staleTime: API_STALE_TIME_MS,
    // keepPreviousData: true, // Remove if not supported
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLead(id),
    onSuccess: () => {
      message.success(t('success:deleteSuccess', { entity: t('leads:lead') }));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate('/sales/leads');
    },
    onError: () => {
      message.error(t('sales:errors.deleteFailed', { entity: t('leads:lead') }));
    },
  });

  // Only show leads without an opportunity
  const filteredLeads = leads.filter(lead => !lead.opportunity);

  const handleTableChange = useCallback((pagination: any) => {
    if (pagination.current > page && hasNext) {
      setPage(page + 1);
    } else if (pagination.current < page && page > 1) {
      setPage(page - 1);
    }
  }, [page, hasNext]);

  const handleRowClick = useCallback((lead: Lead) => {
    navigate(`/sales/leads/${lead.id}`, { state: { lead } });
  }, [navigate]);

  const handleAdd = useCallback(() => {
    setEditingLead(null);
    form.resetFields();
    form.setFieldsValue({ status: 'New' });
    setOpenDialog(true);
  }, [form]);

  const handleEdit = useCallback((lead: Lead) => {
    setEditingLead(lead);
    form.setFieldsValue({
      name: lead.name,
      status: lead.status,
      source: lead.source,
      address: lead.address,
      city: lead.city,
      pincode: lead.pincode,
      country: lead.country,
      description: lead.description,
      notes: lead.notes,
      contacts: lead.contacts.map((contact: Contact) => ({
        name: contact.name,
        position: contact.position,
        email: contact.email,
        phone: contact.phone,
        type: contact.type,
      })),
    });
    setOpenDialog(true);
  }, [form]);

  const handleDelete = useCallback((lead: Lead) => {
    setDeleteId(lead.id);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  }, [deleteId, deleteMutation]);

  const handleCancelDelete = useCallback(() => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingLead(null);
    form.resetFields();
  }, [form]);

  const handleAddOpportunity = useCallback((lead: Lead) => {
    setSelectedLeadId(lead.id);
    opportunityForm.resetFields();
    opportunityForm.setFieldsValue({ lead_id: lead.id });
    setOpenOpportunityDialog(true);
  }, [opportunityForm]);

  // Handler for search input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value), []);

  // Pagination config for AntD Table (no total, just next/prev)
  const pagination = {
    current: page,
    pageSize: PAGE_SIZE,
    total: undefined, // Hide total
    showTotal: undefined,
    showSizeChanger: false,
    showQuickJumper: false,
    onChange: (p: number) => {
      if (p > page && hasNext) setPage(page + 1);
      else if (p < page && page > 1) setPage(page - 1);
    },
    // Hide page size changer and quick jumper
  };

  // Optimistic update for add/edit (handled in mutation, not shown here)
  const handleLeadModalSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    handleCloseDialog();
  }, [queryClient, handleCloseDialog]);

  // Add opportunity mutation
  const addOpportunityMutation = useMutation({
    mutationFn: ({ leadId, opportunity }: { leadId: string; opportunity: any }) => addOrUpdateOpportunity(leadId, opportunity),
    onSuccess: () => {
      message.success(t('success:createSuccess', { entity: t('opportunities.opportunity') }));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setOpenOpportunityDialog(false);
    },
    onError: () => {
      message.error(t('errors.saveFailed', { entity: t('opportunities.opportunity') }));
    },
  });

  // Save handler for OpportunityModal
  const handleOpportunitySave = async () => {
    try {
      const values = await opportunityForm.validateFields();
      const opportunity = {
        stage: values.stage,
        value: values.price,
        expected_close_date: values.follow_up_date instanceof Date ? values.follow_up_date : values.follow_up_date.toDate(),
        units: values.units,
      };
      addOpportunityMutation.mutate({ leadId: selectedLeadId!, opportunity });
    } catch (error) {
      message.error(t('errors.saveFailed', { entity: t('opportunities.opportunity') }));
    }
  };

  // Handler for discovery tab lead view
  const handleViewLead = useCallback((lead: any) => {
    navigate(`/sales/leads/${lead.id}`, { state: { lead } });
  }, [navigate]);

  // Tab items configuration
  const tabItems = [
    {
      key: 'leads',
      label: t('sales:leads.title', 'Leads'),
      children: (
        <div style={{ width: '100%' }}>
          {isLoading && leads.length === 0 ? (
            <Card style={{ marginTop: 24 }}><Skeleton active /></Card>
          ) : (
            <>
            <Row 
              justify="space-between" 
              align="middle" 
              style={{ marginBottom: 24, marginTop: 24}}
              gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]}
            >
              <Col xs={24} sm={16} md={18}>
                <Input
                  placeholder={t('sales:leads.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={8} md={6} style={{ textAlign: 'right' }}>
                <Button
                  type="primary"
                  onClick={handleAdd}
                  icon={<PlusOutlined />}
                >
                  {t('sales:leads.add')}
                </Button>
              </Col>
            </Row>

            <Card variant="borderless" style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
              <LeadsTable
                leads={filteredLeads}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleRowClick}
                onAddOpportunity={handleAddOpportunity}
                loading={isLoading}
                pagination={pagination}
                onTableChange={handleTableChange}
              />
            </Card>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'discover',
      label: t('sales:discovery.title', 'Discover'),
      children: <DiscoveryTab onViewLead={handleViewLead} />,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginTop: 24 }}
      />

      <LeadModal
        open={openDialog}
        lead={editingLead}
        onSuccess={handleLeadModalSuccess}
        onCancel={handleCloseDialog}
      />

      <ConfirmDeleteModal
        open={openDeleteDialog}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title={t('common:confirmDelete')}
        description={t('sales:leads.deleteConfirm')}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
        okButtonProps={{ danger: true }}
      />

      <OpportunityModal
        open={openOpportunityDialog}
        onOk={handleOpportunitySave}
        onCancel={() => setOpenOpportunityDialog(false)}
        form={opportunityForm}
        leads={filteredLeads}
        editingOpportunity={null}
        defaultLeadId={selectedLeadId}
      />
    </div>
  );
};

export default NewLeadsPage; 